import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Dialog,
  Divider,
} from '@mui/material';
import {
  LocationOn,
  Phone,
  Email,
  EventSeat,
  Check,
  ArrowForward,
  ArrowBack,
  Close,
  AccessTime,
  Wifi,
  AcUnit,
  LocalCafe,
  WaterDrop,
  Power,
  LocalParking,
  Security,
  MenuBook,
} from '@mui/icons-material';

// Amenity icons mapping
const amenityIcons = {
  wifi: <Wifi />,
  ac: <AcUnit />,
  'air conditioning': <AcUnit />,
  cafeteria: <LocalCafe />,
  cafe: <LocalCafe />,
  water: <WaterDrop />,
  'drinking water': <WaterDrop />,
  power: <Power />,
  'power backup': <Power />,
  parking: <LocalParking />,
  security: <Security />,
  'reading room': <MenuBook />,
  locker: <Security />,
};

const getAmenityIcon = (amenity) => {
  const key = amenity.toLowerCase();
  return amenityIcons[key] || <Check />;
};

const placeholderImages = [
  'https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=800&q=80',
  'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=800&q=80',
  'https://images.unsplash.com/photo-1568667256549-094345857637?w=800&q=80',
];

function ClassicTheme({ library, plans, accentColor, onBookNow }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const images = library.gallery?.length > 0 ? library.gallery : placeholderImages;
  const defaultDescription = `Welcome to ${library.name}! We provide a peaceful and productive environment for students and professionals.`;

  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#FDF8F3', fontFamily: 'Georgia, serif' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#2C1810', py: 2 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            sx={{ color: '#D4AF37', fontFamily: 'Georgia, serif', fontWeight: 400, textAlign: 'center' }}
          >
            {library.name}
          </Typography>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box sx={{ position: 'relative', height: { xs: '40vh', md: '60vh' } }}>
        <Box
          component="img"
          src={images[currentImageIndex]}
          alt={library.name}
          loading="eager"
          fetchpriority="high"
          decoding="async"
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, rgba(44,24,16,0.2), rgba(44,24,16,0.6))',
          }}
        />

        {images.length > 1 && (
          <>
            <IconButton
              onClick={prevImage}
              sx={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.8)' }}
            >
              <ArrowBack />
            </IconButton>
            <IconButton
              onClick={nextImage}
              sx={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.8)' }}
            >
              <ArrowForward />
            </IconButton>
          </>
        )}

        <Box
          sx={{
            position: 'absolute',
            bottom: 40,
            left: 0,
            right: 0,
            textAlign: 'center',
            color: 'white',
          }}
        >
          <Typography variant="h6" sx={{ fontFamily: 'Georgia, serif', opacity: 0.9, mb: 2 }}>
            <LocationOn sx={{ verticalAlign: 'middle', mr: 1 }} />
            {library.address}
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={onBookNow}
            sx={{
              bgcolor: '#D4AF37',
              color: '#2C1810',
              '&:hover': { bgcolor: '#C19B2C' },
              fontFamily: 'Georgia, serif',
              px: 5,
              py: 1.5,
            }}
          >
            Reserve Your Seat
          </Button>
        </Box>
      </Box>

      {/* About Section */}
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="overline" sx={{ color: accentColor, letterSpacing: 3 }}>
          Welcome To
        </Typography>
        <Typography variant="h3" sx={{ fontFamily: 'Georgia, serif', mb: 4, color: '#2C1810' }}>
          {library.name}
        </Typography>
        <Divider sx={{ width: 100, mx: 'auto', mb: 4, borderColor: accentColor }} />
        <Typography variant="body1" sx={{ lineHeight: 2, color: '#5C4A3D', fontFamily: 'Georgia, serif' }}>
          {library.description || defaultDescription}
        </Typography>

        <Grid container spacing={4} sx={{ mt: 4 }}>
          <Grid item xs={6}>
            <Box sx={{ p: 3, border: '1px solid #D4AF37', borderRadius: 0 }}>
              <EventSeat sx={{ fontSize: 40, color: '#D4AF37', mb: 1 }} />
              <Typography variant="h4" sx={{ fontFamily: 'Georgia, serif', color: '#2C1810' }}>
                {library.capacity}
              </Typography>
              <Typography variant="body2" color="text.secondary">Seats</Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ p: 3, border: '1px solid #D4AF37', borderRadius: 0 }}>
              <AccessTime sx={{ fontSize: 40, color: '#D4AF37', mb: 1 }} />
              <Typography variant="h6" sx={{ fontFamily: 'Georgia, serif', color: '#2C1810' }}>
                Open Daily
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {library.operating_hours || 'Contact for hours'}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Plans Section */}
      <Box sx={{ bgcolor: '#2C1810', py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{ fontFamily: 'Georgia, serif', color: '#D4AF37', textAlign: 'center', mb: 6 }}
          >
            Membership Plans
          </Typography>

          {plans.length > 0 ? (
            <Grid container spacing={4} justifyContent="center">
              {plans.map((plan) => (
                <Grid item xs={12} sm={6} md={3} key={plan.id}>
                  <Card
                    sx={{
                      bgcolor: 'transparent',
                      border: '1px solid #D4AF37',
                      borderRadius: 0,
                      height: '100%',
                    }}
                  >
                    <CardContent sx={{ p: 4, textAlign: 'center' }}>
                      <Typography variant="overline" sx={{ color: '#D4AF37', letterSpacing: 2 }}>
                        {plan.plan_type.replace('_', ' ')}
                      </Typography>
                      <Typography variant="h3" sx={{ color: 'white', fontFamily: 'Georgia, serif', my: 2 }}>
                        ₹{plan.price_cents}
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={onBookNow}
                        sx={{
                          borderColor: '#D4AF37',
                          color: '#D4AF37',
                          '&:hover': { bgcolor: '#D4AF37', color: '#2C1810' },
                          fontFamily: 'Georgia, serif',
                        }}
                      >
                        Select
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ color: 'white', fontFamily: 'Georgia, serif', mb: 3 }}>
                Contact us for membership details
              </Typography>
              <Button
                variant="contained"
                onClick={onBookNow}
                sx={{ bgcolor: '#D4AF37', color: '#2C1810', '&:hover': { bgcolor: '#C19B2C' } }}
              >
                Get in Touch
              </Button>
            </Box>
          )}
        </Container>
      </Box>

      {/* Gallery Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" sx={{ fontFamily: 'Georgia, serif', textAlign: 'center', mb: 6, color: '#2C1810' }}>
          Our Space
        </Typography>
        <Grid container spacing={2}>
          {images.slice(0, 6).map((img, idx) => (
            <Grid item xs={6} md={4} key={idx}>
              <Box
                component="img"
                src={img}
                alt={`Gallery ${idx + 1}`}
                onClick={() => { setCurrentImageIndex(idx); setLightboxOpen(true); }}
                loading="lazy"
                decoding="async"
                sx={{
                  width: '100%',
                  height: 220,
                  objectFit: 'cover',
                  cursor: 'pointer',
                  filter: 'sepia(20%)',
                  transition: 'all 0.3s',
                  '&:hover': { filter: 'sepia(0%)' },
                }}
              />
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Amenities */}
      {library.amenities?.length > 0 && (
        <Box sx={{ bgcolor: '#F5EDE4', py: 8 }}>
          <Container maxWidth="lg">
            <Typography variant="h3" sx={{ fontFamily: 'Georgia, serif', textAlign: 'center', mb: 6, color: '#2C1810' }}>
              Facilities
            </Typography>
            <Grid container spacing={3} justifyContent="center">
              {library.amenities.map((amenity, idx) => (
                <Grid item xs={6} sm={4} md={2} key={idx}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Box sx={{ color: '#D4AF37', mb: 1 }}>
                      {React.cloneElement(getAmenityIcon(amenity), { sx: { fontSize: 36 } })}
                    </Box>
                    <Typography variant="body2" sx={{ fontFamily: 'Georgia, serif', color: '#2C1810' }}>
                      {amenity}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}

      {/* Location & Contact */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography variant="h4" sx={{ fontFamily: 'Georgia, serif', mb: 4, color: '#2C1810' }}>
              Find Us
            </Typography>
            <Box sx={{ height: 300, bgcolor: '#E8DFD3' }}>
              {library.lat && library.lng ? (
                <iframe
                  title="Location"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${library.lat},${library.lng}&zoom=15`}
                />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography color="text.secondary">Map unavailable</Typography>
                </Box>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h4" sx={{ fontFamily: 'Georgia, serif', mb: 4, color: '#2C1810' }}>
              Contact
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <LocationOn sx={{ color: '#D4AF37' }} />
                <Typography sx={{ fontFamily: 'Georgia, serif' }}>{library.address}</Typography>
              </Box>
              {library.contact_phone && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Phone sx={{ color: '#D4AF37' }} />
                  <Typography sx={{ fontFamily: 'Georgia, serif' }}>
                    <a href={`tel:${library.contact_phone}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                      {library.contact_phone}
                    </a>
                  </Typography>
                </Box>
              )}
              {library.contact_email && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Email sx={{ color: '#D4AF37' }} />
                  <Typography sx={{ fontFamily: 'Georgia, serif' }}>
                    <a href={`mailto:${library.contact_email}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                      {library.contact_email}
                    </a>
                  </Typography>
                </Box>
              )}
            </Box>
            <Button
              variant="contained"
              size="large"
              onClick={onBookNow}
              sx={{
                mt: 4,
                bgcolor: '#2C1810',
                '&:hover': { bgcolor: '#1A0F0A' },
                fontFamily: 'Georgia, serif',
              }}
            >
              Reserve Your Seat
            </Button>
          </Grid>
        </Grid>
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: '#2C1810', py: 4, textAlign: 'center' }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Georgia, serif' }}>
          Powered by{' '}
          <a href="https://bookmylibrary.in" target="_blank" rel="noopener noreferrer" style={{ color: '#D4AF37' }}>
            BookMyLibrary
          </a>
        </Typography>
      </Box>

      {/* Lightbox */}
      <Dialog open={lightboxOpen} onClose={() => setLightboxOpen(false)} maxWidth="lg" fullWidth>
        <IconButton onClick={() => setLightboxOpen(false)} sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1 }}>
          <Close />
        </IconButton>
        <Box component="img" src={images[currentImageIndex]} alt="Gallery" loading="lazy" decoding="async" sx={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }} />
      </Dialog>
    </Box>
  );
}

export default ClassicTheme;
